import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { CATEGORIES } from "../../data/services";
import { BOOKING_URL, IMG } from "../../data/site";
import { Reveal } from "../Reveal";

export const Hero = () => (
  <section className="relative h-[calc(100svh-144px)] md:h-[calc(100svh-160px)] lg:h-[calc(100vh-201px)] min-h-[560px] overflow-hidden bg-neutral-900" data-testid="hero-section">
    <img src={IMG.hero} alt="Private facial treatment at Lux Veritae Skin Spa" className="slowzoom absolute inset-0 h-full w-full object-cover grayscale-[35%]" />
    <div className="absolute inset-0 bg-black/40" />
    <div className="relative mx-auto flex h-full max-w-[1400px] flex-col items-center justify-center px-4 text-center text-white md:px-8">
      <p className="rise text-[11px] uppercase tracking-[0.4em] text-neutral-200" data-testid="hero-slogan">Confident enough to leave the lights on</p>
      <h1 className="rise d1 font-display mt-6 max-w-4xl text-5xl font-light leading-[1.05] sm:text-6xl lg:text-7xl" data-testid="hero-headline">
        Personalized aesthetics, <em className="font-light">expertly</em> delivered.
      </h1>
      <p className="rise d2 mt-8 max-w-xl text-sm font-light leading-relaxed text-neutral-200 md:text-base">
        Advanced lasers, medical-grade skincare, permanent makeup and physician-supervised weight loss — one private address for every step of your skin's story.
      </p>
      <div className="rise d3 mt-10 flex flex-col gap-4 sm:flex-row">
        <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="bg-white px-8 py-4 text-[11px] font-medium uppercase tracking-[0.2em] text-neutral-950 transition-colors hover:bg-neutral-200" data-testid="hero-book-btn">Book an Appointment</a>
        <Link to="/contact" className="btn-ghost-light" data-testid="hero-consult-btn">Request a Consultation</Link>
      </div>
    </div>
    <div className="absolute bottom-8 left-4 flex items-center gap-3 text-white md:left-8">
      <span className="h-px w-10 bg-white/70" />
      <span className="text-[10px] uppercase tracking-[0.3em]">The World of Lux Veritae</span>
    </div>
  </section>
);

export const Intro = () => (
  <section className="bg-white py-28 md:py-36" data-testid="intro-section">
    <Reveal className="mx-auto max-w-3xl px-6 text-center">
      <p className="kicker mb-8" data-testid="intro-slogan">Confident enough to leave the lights on</p>
      <p className="font-display text-3xl font-light italic leading-snug text-neutral-900 md:text-4xl">
        Step into quiet luxury. Welcome to Lux Veritae Skin Spa, where expertise, discretion and a plan written only for you come together under one roof.
      </p>
    </Reveal>
  </section>
);

const Card = ({ c }) => (
  <Link to={`/treatments#${c.id}`} className="group block w-[78vw] shrink-0 snap-start sm:w-[360px]" data-testid={`featured-card-${c.id}`}>
    <div className="aspect-[3/4] overflow-hidden bg-neutral-100">
      <img src={c.image} alt={c.name} className="img-zoom h-full w-full object-cover" loading="lazy" />
    </div>
    <p className="kicker mt-6">{c.kicker}</p>
    <h3 className="font-display mt-2 text-3xl font-light text-neutral-950">{c.name}</h3>
    <p className="mt-3 text-sm font-light leading-relaxed text-neutral-600">{c.tagline}</p>
    <span className="link-under mt-5">Explore More</span>
  </Link>
);

export const Featured = () => {
  const ref = useRef(null);
  const [page, setPage] = useState(1);
  const total = CATEGORIES.length;
  const scroll = (dir) => {
    const el = ref.current;
    el.scrollBy({ left: dir * 400, behavior: "smooth" });
  };
  const onScroll = () => {
    const el = ref.current;
    const ratio = el.scrollLeft / Math.max(1, el.scrollWidth - el.clientWidth);
    setPage(Math.round(ratio * (total - 1)) + 1);
  };
  return (
    <section className="bg-[hsl(var(--stone))] py-24 md:py-32" data-testid="featured-section">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8">
        <Reveal className="mb-14 flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <p className="kicker">Where to Begin</p>
            <h2 className="font-display mt-3 text-4xl font-light text-neutral-950 md:text-5xl">Featured Treatments</h2>
            <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-neutral-600 md:text-base">
              Step into a world of advanced technology, refined artistry and individual attention, where every visit is shaped around the results you want.
            </p>
          </div>
          <Link to="/treatments" className="link-under self-start md:self-end" data-testid="featured-view-all">View the Full Menu</Link>
        </Reveal>
        <div ref={ref} onScroll={onScroll} className="no-scrollbar flex snap-x snap-mandatory gap-8 overflow-x-auto pb-4" data-testid="featured-carousel">
          {CATEGORIES.map((c) => <Card key={c.id} c={c} />)}
        </div>
        <div className="mt-12 flex items-center justify-center gap-8">
          <button onClick={() => scroll(-1)} aria-label="Previous" data-testid="featured-prev-btn" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-neutral-700 transition-colors hover:text-neutral-950"><ArrowLeft size={14} strokeWidth={1.25} /> Previous</button>
          <span className="font-display text-lg text-neutral-950" data-testid="featured-page-indicator">{page} <span className="text-neutral-400">/ {total}</span></span>
          <button onClick={() => scroll(1)} aria-label="Next" data-testid="featured-next-btn" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-neutral-700 transition-colors hover:text-neutral-950">Next <ArrowRight size={14} strokeWidth={1.25} /></button>
        </div>
      </div>
    </section>
  );
};
